import type { MapPageDom } from './types';

/**
 * Finds and validates the DOM structure prepared for the new `/map` screen.
 *
 * @returns Resolved DOM references or `null` when this document is not a complete `/map` screen.
 */
export function getMapPageDom(): MapPageDom | null {
  const root = document.querySelector('[data-map-page]');
  if (!(root instanceof HTMLElement)) {
    return null;
  }

  const legend = root.querySelector('[data-map-legend]');
  const legendItems = root.querySelector('[data-map-legend-items]');
  const legendStatus = root.querySelector('[data-map-legend-status]');
  const scrollContainer = root.querySelector('[data-map-scroll-container]');
  const canvas = root.querySelector('[data-map-canvas]');
  const canvasPlaceholder = root.querySelector('[data-map-canvas-placeholder]');
  const error = root.querySelector('[data-map-error]');
  const errorMessage = root.querySelector('[data-map-error-message]');
  const errorRetryButton = root.querySelector('[data-map-error-retry]');
  const totalDebtSection = root.querySelector('[data-map-total-debt-section]');
  const totalDebtText = root.querySelector('[data-map-total-debt-text]');
  const tooltipRoot = document.querySelector('[data-map-tooltip-root]');
  const tooltipContent = document.querySelector('[data-map-tooltip-content]');
  const pageBody = document.body;
  const dialogRoot = document.querySelector('[data-map-dialog-root]');
  const dialogContent = document.querySelector('[data-map-dialog-content]');

  if (
    !(legend instanceof HTMLElement) ||
    !(legendItems instanceof HTMLElement) ||
    !(legendStatus instanceof HTMLElement) ||
    !(scrollContainer instanceof HTMLElement) ||
    !(canvas instanceof HTMLElement) ||
    !(canvasPlaceholder instanceof HTMLElement) ||
    !(error instanceof HTMLElement) ||
    !(errorMessage instanceof HTMLElement) ||
    !(errorRetryButton instanceof HTMLButtonElement) ||
    !(totalDebtSection instanceof HTMLElement) ||
    !(totalDebtText instanceof HTMLElement) ||
    !(tooltipRoot instanceof HTMLElement) ||
    !(tooltipContent instanceof HTMLElement) ||
    !(pageBody instanceof HTMLBodyElement) ||
    !(dialogRoot instanceof HTMLElement) ||
    !(dialogContent instanceof HTMLElement)
  ) {
    return null;
  }

  return {
    root,
    legend,
    legendItems,
    legendStatus,
    scrollContainer,
    canvas,
    canvasPlaceholder,
    error,
    errorMessage,
    errorRetryButton,
    totalDebtSection,
    totalDebtText,
    tooltipRoot,
    tooltipContent,
    pageBody,
    dialogRoot,
    dialogContent,
  };
}

/**
 * Updates the current frontend architecture stage on the root container.
 *
 * @param params Page root and diagnostic stage label used by CSS, tests and frontend observability.
 * @returns Nothing. Updates only the root `data-map-stage` attribute.
 */
export function setMapPageStage(params: { mapDom: MapPageDom; stage: string }): void {
  params.mapDom.root.dataset.mapStage = params.stage;
}

/**
 * Replaces the text inside the total debt block.
 *
 * @param params Page DOM and backend-formatted debt text for the footer summary.
 * @returns Nothing. Replaces only the total-debt text node.
 */
export function setTotalDebtText(params: { mapDom: MapPageDom; text: string }): void {
  params.mapDom.totalDebtText.textContent = params.text;
}

/**
 * Toggles visibility of the SVG placeholder inside the canvas host.
 *
 * @param params Page DOM and whether the SSR canvas placeholder must be hidden.
 * @returns Nothing. Toggles only the placeholder visibility class.
 */
export function setCanvasPlaceholderHidden(params: { mapDom: MapPageDom; hidden: boolean }): void {
  params.mapDom.canvasPlaceholder.classList.toggle('hidden', params.hidden);
}

/**
 * Replaces the helper text above legend cards.
 *
 * @param params Page DOM and user-facing legend status text.
 * @returns Nothing. Replaces only the legend status text node.
 */
export function setLegendStatusText(params: { mapDom: MapPageDom; text: string }): void {
  params.mapDom.legendStatus.textContent = params.text;
}
